import { Datagrid, DateField, FunctionField, List, ReferenceField, SelectInput, Show, SimpleShowLayout, TextField, TextInput } from 'react-admin';
import { TopToolbar, useListContext, useNotify, useRefresh } from 'react-admin';
import { Button } from '@mui/material';
import inMemoryJWT from './inMemoryJWT';
import axios from 'axios';
import { apiBaseUrl } from './config';

const deviceEventFilters = [
    <TextInput source="q" label="Search" alwaysOn />,
    <TextInput source="device_id" label="Device ID" />,
    <SelectInput source="model" label="Device Type" choices={[
        { id: 'T5304V2', name: 'T5304V2' },
        { id: 'DC082', name: 'DC082' },
        { id: 'T8112B', name: 'T8112B' },
    ]} />,
    <SelectInput source="status" label="Status" choices={[
        { id: 'open', name: 'Open' },
        { id: 'resolved', name: 'Resolved' },
    ]} />,
    <SelectInput source="event_type" label="Event Type" choices={[
        { id: 'CAMERA_ABNORMAL', name: 'Camera Abnormal' },
        { id: 'CAMERA_NORMAL', name: 'Camera Normal' },
        { id: 'DISK_ABNORMAL', name: 'Disk Abnormal' },
        { id: 'DISK_NORMAL', name: 'Disk Normal' },
        { id: 'CALIBRATION_SUCCESS', name: 'Calibration Success' },
        { id: 'CALIBRATION_FAILED', name: 'Calibration Failed' },
        { id: 'CALIBRATION_LOST', name: 'Calibration Lost' },
        { id: 'CALIBRATION_RESET', name: 'Calibration Reset' },
        { id: 'CALIBRATION_RESTORE_REQUESTED', name: 'Calibration Restore Requested' },
        { id: 'CALIBRATION_RECONCILED', name: 'Calibration Reconciled at Boot' },
        { id: 'HARD_ACCELERATION', name: 'Hard Acceleration' },
        { id: 'HARD_DECELERATION', name: 'Hard Brake' },
        { id: 'SHARP_TURN', name: 'Sharp Turn' },
        { id: 'ROLLOVER', name: 'Rollover' },
        { id: 'CRASH', name: 'Crash' },
        // ET12_CONFIGURATION_MONITORING_SPEC.md sections 15/16 (device SDK repo root) -
        // append-only transition events, no open/resolved lifecycle.
        { id: 'CONFIGURATION_MISMATCH', name: 'Configuration Mismatch' },
        { id: 'CONFIGURATION_NORMAL', name: 'Configuration Normal' },
        { id: 'DEVICE_OFFLINE', name: 'Device Offline' },
        { id: 'DEVICE_ONLINE', name: 'Device Online' },
        { id: 'HEALTH_ALERT', name: 'Health Alert' },
        { id: 'HEALTH_NORMAL', name: 'Health Normal' },
    ]} />,
    <SelectInput source="issue_type" label="Issue" choices={[
        { id: 'camera_not_normal', name: 'Camera Not Normal' },
        { id: 'camera_normal', name: 'Camera Normal' },
        { id: 'disk_abnormal', name: 'Disk Abnormal' },
        { id: 'disk_normal', name: 'Disk Normal' },
        { id: 'calibration_success', name: 'Calibration Success' },
        { id: 'calibration_failed', name: 'Calibration Failed' },
        { id: 'calibration_lost', name: 'Calibration Lost' },
        { id: 'calibration_reset', name: 'Calibration Reset' },
        { id: 'calibration_restore_requested', name: 'Calibration Restore Requested' },
        { id: 'calibration_reconciled', name: 'Calibration Reconciled at Boot' },
        { id: 'hard_acceleration', name: 'Hard Acceleration' },
        { id: 'hard_deceleration', name: 'Hard Brake' },
        { id: 'sharp_turn', name: 'Sharp Turn' },
        { id: 'rollover', name: 'Rollover' },
        { id: 'crash', name: 'Crash' },
        { id: 'configuration_mismatch', name: 'Configuration Mismatch' },
        { id: 'configuration_normal', name: 'Configuration Normal' },
        { id: 'device_offline', name: 'Device Offline' },
        { id: 'device_online', name: 'Device Online' },
        { id: 'health_alert', name: 'Health Alert' },
        { id: 'health_normal', name: 'Health Normal' },
    ]} />,
];

function formatIssue(issueType) {
    if (issueType === 'camera_not_normal') return 'Camera not normal';
    if (issueType === 'camera_normal') return 'Camera returned to normal';
    if (issueType === 'disk_abnormal') return 'Disk abnormal';
    if (issueType === 'disk_normal') return 'Disk returned to normal';
    if (issueType === 'calibration_success') return 'Calibration succeeded';
    if (issueType === 'calibration_failed') return 'Calibration failed to converge';
    if (issueType === 'calibration_lost') return 'Calibration lost (orientation changed)';
    if (issueType === 'calibration_reset') return 'Calibration reset';
    if (issueType === 'calibration_restore_requested') return 'Calibration restore requested';
    if (issueType === 'calibration_reconciled') return 'Calibration reconciled at boot';
    if (issueType === 'hard_acceleration') return 'Hard acceleration';
    if (issueType === 'hard_deceleration') return 'Hard brake';
    if (issueType === 'sharp_turn') return 'Sharp turn';
    if (issueType === 'rollover') return 'Rollover';
    if (issueType === 'crash') return 'Crash';
    if (issueType === 'configuration_mismatch') return 'Configuration mismatch';
    if (issueType === 'configuration_normal') return 'Configuration restored to match template';
    if (issueType === 'device_offline') return 'Device offline';
    if (issueType === 'device_online') return 'Device back online';
    if (issueType === 'health_alert') return 'Device health abnormal';
    if (issueType === 'health_normal') return 'Device health normal';
    return issueType || '-';
}

function formatDetail(detail) {
    if (!detail) return '-';
    if (typeof detail === 'string') return detail;
    try {
        return JSON.stringify(detail);
    } catch (err) {
        return '-';
    }
}

/* "Clear All Events" (ET12_CONFIGURATION_MONITORING_SPEC.md section 17) - scoped to whatever
   device_id the list is currently filtered to, same as the GSensor tab's own clear button, so
   this can never wipe the whole fleet's event history in one click. Disabled until a device_id
   filter is actually applied. Deletes history only (POST /device_events/clear already
   excludes source:'mqtt_dashcam_event' server-side - g-sensor events have their own separate
   clear button and are untouched by this one) - never changes device health, configuration
   state, or resolves a real ongoing problem. */
function DeviceEventListActions() {
    const { filterValues } = useListContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const deviceId = filterValues && filterValues.device_id;

    function handleClear() {
        const token = inMemoryJWT.getToken();
        if (!token) {
            notify("No token found", { type: "error" });
            return;
        }
        if (!window.confirm("Clear all Device Events for " + deviceId + "? This cannot be undone.")) {
            return;
        }
        axios.post(apiBaseUrl + '/device_events/clear', { device_id: deviceId }, {
            headers: { 'Content-Type': 'application/json', 'x-access-token': `${token}` }
        }).then(function() {
            notify("Device events cleared for " + deviceId, { type: 'success' });
            refresh();
        }).catch(function(error) {
            notify("Clear events failed: " + error, { type: 'error' });
        });
    }

    return (
        <TopToolbar>
            <Button variant="contained" color="error" size="small" disabled={!deviceId} onClick={handleClear}
                title={!deviceId ? "Filter by Device ID first" : undefined}>
                Clear All Events
            </Button>
        </TopToolbar>
    );
}

export const DeviceEventList = () => (
    <List perPage={50} sort={{ field: 'created_at', order: 'DESC' }} filters={deviceEventFilters}
        actions={<DeviceEventListActions />}>
        <Datagrid rowClick="show">
            <ReferenceField source="device_id" reference="devices" />
            <TextField source="device_id" />
            <TextField label="Event Type" source="event_type" />
            <FunctionField label="Issue" render={record => formatIssue(record.issue_type)} />
            <TextField source="status" />
            <TextField source="severity" />
            <TextField source="message" />
            <DateField label="First Detected" source="first_detected_at" showTime />
            <DateField label="Resolved" source="resolved_at" showTime />
            <DateField label="Created" source="created_at" showTime />
        </Datagrid>
    </List>
);

export const DeviceEventShow = () => (
    <Show>
        <SimpleShowLayout>
            <ReferenceField source="device_id" reference="devices" />
            <TextField source="device_id" />
            <TextField label="Event Type" source="event_type" />
            <FunctionField label="Issue" render={record => formatIssue(record.issue_type)} />
            <TextField source="status" />
            <TextField source="severity" />
            <TextField source="message" />
            <TextField source="source" />
            <DateField label="First Detected" source="first_detected_at" showTime />
            <DateField label="Resolved" source="resolved_at" showTime />
            <DateField label="Created" source="created_at" showTime />
            <DateField label="Updated" source="updated_at" showTime />
            <FunctionField label="Detail" render={record => formatDetail(record.detail)} />
        </SimpleShowLayout>
    </Show>
);
